/** Centralized regex + text parsers for partner scheduling emails/details. */
export const PARTNER_SCHEDULING_PATTERNS = {
  email: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /** First amount after `Travel:` — ignores any currency words/symbols before the number. */
  travelLine: /^\s*Travel\s*:\s*[^\d\r\n]*(\d+(?:[.,]\d{1,2})?)/im,
  /** Same idea for explicit travel cost / travel charge lines (no generic `cost:` to avoid false positives). */
  travelCostLabeled: /(?:travel\s*cost|travel\s*charge|travelling?\s*cost)\s*[:\-]\s*[^\d\r\n]*(\d+(?:[.,]\d{1,2})?)/im,
  etaOtrsLike: /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2})?)/,
  etaDmy: /\b(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:\s+(\d{1,2}:\d{2}(?::\d{2})?))?\b/,
  etaDmy12h: /\b(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\b/i,
  etaEmbeddedIso: /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?/,
  whitespace: /\s+/g,
  phoneNonDigitsExceptLeadingPlus: /(?!^\+)[^\d]/g,
} as const;

export const PARTNER_RESPONSE_FIELD_LABELS = {
  eta: ['ETA', 'Estimated Time of Arrival', 'Engineer Required For'],
  // "Name" added for FE Details blocks that use short labels (e.g. OSGTW format)
  feName: ['FE Name', 'Engineer Name', 'Field Engineer Name', 'Name', 'Site Contact name'],
  // "Number" added for partners who use Number: instead of Phone:
  fePhone: ['FE Phone', 'Phone', 'Mobile', 'Contact Number', 'Number', 'Site Contact phone'],
  feEmail: ['FE Email', 'Engineer Email', 'Site Contact email'],
} as const;

export const FE_DETAILS_EMAIL_LABELS = ['FE Email', 'Engineer Email', 'Email'] as const;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeWhitespace(value?: string | null) {
  return String(value ?? '')
    .trim()
    .replace(PARTNER_SCHEDULING_PATTERNS.whitespace, ' ');
}

/** OTRS plain-text often prefixes links/emails with `[2]`. Strip leading refs from a field line. */
export function stripOtrsPlainTextRefs(value: string) {
  return String(value)
    .replace(/^(?:\[\d+\]\s*)+/, '')
    .trim();
}

export function extractEmail(value?: string | null) {
  const peeled = String(value ?? '').replace(/\[\d+\]\s*/g, ' ');
  return peeled.match(PARTNER_SCHEDULING_PATTERNS.email)?.[0]?.toLowerCase();
}

export function extractAllEmails(value?: string | null): string[] {
  const peeled = String(value ?? '').replace(/\[\d+\]\s*/g, ' ');
  const regex = new RegExp(PARTNER_SCHEDULING_PATTERNS.email.source, 'gi');
  const matches = peeled.match(regex);
  return matches ? matches.map(m => m.toLowerCase()) : [];
}

/**
 * Text between `FE Details` (with or without colon) and `Applicable Charges` (or end).
 * Handles both structured OTRS template format and free-text partner replies like OSGTW.
 */
export function extractFeDetailsSection(body: string) {
  const text = String(body);
  // Match "FE Details" with optional colon/whitespace, followed by a newline
  const m = /\bFE Details\s*:?\s*\r?\n/i.exec(text);
  if (!m) return '';
  const rest = text.slice(m.index + m[0].length);
  const stop = rest.search(/\r?\n\s*(?:Applicable Charges|Applicable\b)/i);
  return (stop === -1 ? rest : rest.slice(0, stop)).trim();
}

/** First line after any of the labels (start of line), value is remainder of that line (trimmed). */
export function extractField(body: string, fieldNames: readonly string[]) {
  const escaped = fieldNames.map(escapeRegExp).join('|');
  const regex = new RegExp(`(?:^|\\n)\\s*(?:${escaped})\\s*(?:[:\\-]\\s*|\\s{2,})(.+)`, 'i');
  return body.match(regex)?.[1]?.trim();
}

/**
 * Keeps the partner-authored top section; drops all quoted reply threads.
 */
export function splitPartnerReplyHead(body: string): string {
  const text = String(body ?? '');

  const markers: RegExp[] = [
    // Outlook: optional underscores then From/Sent/To/Subject block
    /\r?\n_{10,}\s*\r?\n(?=\s*From:\s)/i,
    // Outlook without underscores: From: + optional Sent: + optional To: + Subject:
    /\r?\nFrom:\s.+(?:\r?\n(?:Sent|Date):\s.+)?(?:\r?\nTo:\s.+)?\r?\nSubject:/im,
    // Gmail / CN client: "On <date> <name> wrote:"
    /\r?\nOn\s.+\swrote:\s*\r?\n/i,
    // Plain horizontal rule
    /\r?\n-{5,}\s*Original Message\s*-{5,}\s*\r?\n/i,
    // Generic long dash/underscore separator
    /\r?\n[_\-]{28,}\s*\r?\n/,
  ];

  let cut = text.length;
  for (const re of markers) {
    const m = re.exec(text);
    if (m?.index != null && m.index < cut) cut = m.index;
  }
  return text.slice(0, cut).trim();
}

export function extractTravelCost(body: string): { amount?: number; raw?: string } {
  const line =
    body.match(PARTNER_SCHEDULING_PATTERNS.travelLine) ?? body.match(PARTNER_SCHEDULING_PATTERNS.travelCostLabeled);
  if (!line?.[1]) return {};
  const normalized = line[1].replace(',', '.');
  return { amount: Number(normalized), raw: line[0] };
}

export function normalizeFePhone(phone?: string | null) {
  const raw = normalizeWhitespace(phone);
  if (!raw) return '';
  const compact = raw.replace(PARTNER_SCHEDULING_PATTERNS.phoneNonDigitsExceptLeadingPlus, '');
  if (!compact) return '';
  if (compact.startsWith('+')) return compact;
  if (compact.startsWith('00')) return `+${compact.slice(2)}`;
  return `+${compact}`;
}

export function normalizeSchedulingText(value?: string) {
  return (value ?? '').replace(/\u00A0/g, ' ').trim();
}

export function normalizeSchedulingKey(value?: string) {
  return normalizeSchedulingText(value)
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
